import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Gallery from './components/Gallery';
import Description from './components/Description';
import Footer from './components/Footer';
import './styles/App.css';

interface Photo {
  id: number;
  filename: string;
  original_name: string;
  sort_order: number;
  uploaded_at: string;
}

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3000/api';

function App() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track visitor on page load (non-blocking)
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        await fetch(`${API_BASE}/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pagePath: window.location.pathname,
            referrer: document.referrer
          })
        });
      } catch {
        // Silently fail - don't impact user experience
      }
    };
    trackVisitor();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [photosRes, descRes] = await Promise.all([
          fetch(`${API_BASE}/photos`),
          fetch(`${API_BASE}/description`)
        ]);

        if (!photosRes.ok || !descRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const photosData = await photosRes.json();
        const descData = await descRes.json();

        setPhotos(photosData);
        setDescription(descData.description);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Unable to load property data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading property details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="app">
      <Header />
      <main>
        <Gallery photos={photos} apiBase={API_BASE} />
        <Description content={description} />
      </main>
      <Footer />
    </div>
  );
}

export default App;
